import { Spinner, Row, Col, Badge, Container, Nav, Navbar} from "react-bootstrap";
import { Link, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ClaimFormDto } from "../types"
import { getClaimDetail, updateClaimDetail } from "../api/ClaimAPI";
import { useNavigate } from "react-router-dom";
import AlertMessage from "../../../components/AlertMessage";
import { useQueryClient } from "@tanstack/react-query";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import { ClaimStatusEnum } from "../types/ClaimStatusEnum";
import { useNavigationGuard } from "../../../contexts/NavigationGuardContext";
import { GenericSaveButton } from "../../../components/common/GenericSaveButton";
import { AppDispatch, RootState } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedFilters } from "../../../redux/filtersSlice";
import { PERMISSIONS } from "../../../constants/permissions";

export default function EditClaim() {
    const { t } = useTranslation(['common','claim']);
    const { idClaim } = useParams<{ idClaim: string }>();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<{ claimDeleteReasonId?: string, closingDate?: string }>({});

    const [searchParams, setSearchParams] = useSearchParams();
    const from = searchParams.get('from');
    const mode = searchParams.get('mode');
    const isViewMode = mode == "view";
    
    const dispatch = useDispatch<AppDispatch>();

    const [claimUpdate, setClaimUpdate] = useState<ClaimFormDto>();

    const navigate = useNavigate()
    const { setNavigationGuard, navigateWithGuard } = useNavigationGuard();
    const [messageError, setMessageError] = useState<string | null>(null);
    const currentStatus = useRef(0); 
    const initialClaimSnapshot = useRef<string | null>(null);
    const queryClient = useQueryClient();
    const [sendEmail, setSendEmail] = useState(false);
    const [hideClaimActionButtons, setHideClaimActionButtons] = useState(false);

    const [selectedSection, setSelectedSection] = useState<string>("data")

    const [refreshKey, setRefreshKey] = useState(0);
    
    const currentUserPermissions = useSelector((state: RootState) => state.currentUserInfo.data.permissions);

    const reloadClaim = () => {
        setRefreshKey(prev => prev + 1);
    };

    useEffect(() => {
        if (!idClaim) return;
        setLoading(true);
        getClaimDetail(idClaim)
            .then(data => {
                let professionals = data.professionals ?? [];

                const sortedProfessionals = professionals
                    .slice()
                    .sort((a, b) =>
                        (a.isHeadOfDepartment ? 1 : 0) -
                        (b.isHeadOfDepartment ? 1 : 0)
                    );
                    
                setClaimUpdate({
                    ...data,
                    professionals: sortedProfessionals
                });

                initialClaimSnapshot.current = JSON.stringify({
                    ...data,
                    professionals: sortedProfessionals
                });

                currentStatus.current = data.status;
            })
            .catch((error: ApiErrorResponse) => {
                const fetchError = error as ApiErrorResponse;
                setMessageError(fetchError.message || t("claim:requestError.errorBadRequest"))
                
            })
            .finally(() => {
                setLoading(false);
            });

    }, [idClaim, refreshKey]);

    const invalidateQueryDashboard = async () => {
        queryClient.invalidateQueries({
            queryKey: ['getHistoricClaims'],
            exact: false,
            refetchType: 'all',
        });
        queryClient.invalidateQueries({
            queryKey: ['claimStatus'],
            exact: false,
            refetchType: 'all',
        });
        queryClient.invalidateQueries({
            queryKey: ['ClaimStatusLineChartData'],
            exact: false,
            refetchType: 'all',
        });
        queryClient.invalidateQueries({
            queryKey: ['ClaimServiceIndicatorBarChartData'],
            exact: false,
            refetchType: 'all',
        });
        queryClient.invalidateQueries({
            queryKey: ['claimIndicatorsTimeTracking'],
            exact: false,
            refetchType: 'all',
        });
    } 

    const clearFilter = () => {
        dispatch(setSelectedFilters({
            table: "claimsForPersonByAffectedPeople",
            filters: []
        }));

        dispatch(setSelectedFilters({
            table: "claimsForPersonByClaimantClaims",
            filters: []
        }));
    }

    const handleSaveClaim = async () => {
        setLoading(true);
        setMessageError(null);
        try {
            const newErrors: { claimDeleteReasonId?: string, closingDate?: string, organizationalStructureId?: string } = {};
            if (claimUpdate?.status === ClaimStatusEnum.Closed) {
                if (claimUpdate?.claimDeleteReasonId === null) {
                    newErrors.claimDeleteReasonId = t("common:form.requiredFieldForm");
                }
                if (claimUpdate?.closingDate === null) {
                    newErrors.closingDate = t("common:form.requiredFieldForm");
                } else if (new Date(claimUpdate.closingDate!) > new Date()) {
                    newErrors.closingDate = t("claim:messageErrorValidationClosingDate"); 
                }
            }
            if (!claimUpdate?.organizationalStructureId){
                newErrors.organizationalStructureId = t("common:form.requiredFieldForm");
            }
            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                setMessageError(t("common:form.errorMandatoryFieldsToInform"));
                return;
            }

            claimUpdate!.topics = claimUpdate?.topics?.filter(t => t.topicId !== -2) ?? [];
            const response = await updateClaimDetail(idClaim!, claimUpdate!);
            if (from == 'alert') {
                await queryClient.invalidateQueries({
                    queryKey: ['alertClaim'],
                    exact: false,
                    refetchType: 'all'
                });
                navigate(`/alertes?request=${idClaim}`);
            }
            else {
                setLoading(false);
                invalidateQueryDashboard();
                clearFilter();
                let message = t("claim:updateClaimSaveSuccessfully");
                if (response.isAlertCreated) {
                    message = message
                        .concat("\n")
                        .concat(t("claim:updateClaimCreateAlertSuccessfully"));
                }

                navigate('/claims/historic', { state: { message: message } });
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setMessageError(fetchError.message || t("claim:requestError.errorBadRequest"))
            setLoading(false);
        }
    }

    const onGoBackClick = async () => {
        clearFilter();
        if (from == 'alert')
            navigateWithGuard(`/alertes?request=${idClaim}`);
        else
            navigateWithGuard("/claims/historic");
    }

    const additionalPath = from ? `?from=${from}` + (mode ? `&mode=${mode}` : "") : "";
    const MENU_ITEMS = [
        { name: t("claim:menuClaimDatails"), link: `/edit-claim/${idClaim}/data${additionalPath}`, section: 'data', notifications: undefined },
        { name: t("claim:menuClaimDocuments"), link: `/edit-claim/${idClaim}/documents${additionalPath}`, section: 'documents', notifications: undefined },
        { name: t("claim:menuFollowUp"), link: `/edit-claim/${idClaim}/follow-up${additionalPath}`, section: 'follow-up', notifications: undefined },
        { name: t("claim:menuClaimRecommendations"), link: `/edit-claim/${idClaim}/recomendations${additionalPath}`, section: 'recomendations', notifications: undefined },
    ];

    useEffect(() => {
        const matchingItem = MENU_ITEMS.find((item) => location.pathname.endsWith(`/${item.section}`));
        if (matchingItem) {
            setSelectedSection(matchingItem.section);
            setSendEmail(false);
            setHideClaimActionButtons(false);
        } else {
            setSelectedSection("");
        }
    }, [location.pathname]);


    useEffect(() => {
        const hasChanges = !!claimUpdate &&
            !!initialClaimSnapshot.current &&
            JSON.stringify(claimUpdate) !== initialClaimSnapshot.current;

        setNavigationGuard({
            enabled: hasChanges,
            message: t("claim:modal.modalExitWithUnsavedEditClaim"),
            messageButtonSave: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonSave"),
            messageButtonClose: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonClose"),
            onSave: async () => {
                try {
                    await handleSaveClaim();
                    return true;
                } catch {
                    return false;
                }
            }
        });

        return () => {
            setNavigationGuard({
                enabled: false,
                message: "",
                messageButtonSave: "",
                messageButtonClose: ""
            });
        };
    }, [claimUpdate, setNavigationGuard, t]);
    
    return (
        <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>  
            
            <Row className="flex-grow-1 align-items-stretch" style={{ minHeight: 0 }}>
                <Col xs={8} md={8}>
                    <h2>{t("claim:detail.pageEditDetailTitle")}# {idClaim}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    {isViewMode ? 
                        <GenericSaveButton
                            onClick={() => {
                                searchParams.delete('mode');
                                setSearchParams(searchParams);
                            }}
                            onCancelClick={() => navigate(`/alertes?request=${idClaim}`)}
                            cancelLabel={t("common:buttons.buttonBack")}
                            saveLabel={t("common:buttons.buttonEdit")}
                            showSaveButton={!hideClaimActionButtons && currentUserPermissions.length > 0 && currentUserPermissions.includes(PERMISSIONS.CLAIMS.EDIT)}
                            showCancelButton={!hideClaimActionButtons}
                            className="ms-2"
                        /> :
                        <GenericSaveButton
                            onClick={handleSaveClaim}
                            onCancelClick={onGoBackClick}
                            cancelLabel={t(currentStatus.current !== ClaimStatusEnum.Closed || claimUpdate?.status !== ClaimStatusEnum.Closed ? "common:buttons.buttonCancel" : "common:buttons.buttonBack")}
                            className="ms-2"
                            showSaveButton={!sendEmail && !hideClaimActionButtons}
                            showCancelButton={!sendEmail && !hideClaimActionButtons}
                        />
                    }
                </Col>
            </Row>
            {messageError && (
                <Row>
                    <Col xs={12} className="mt-4">
                        <AlertMessage message={messageError} type={"danger"} />
                    </Col>
                </Row>
            )}
            <Row>
                {claimUpdate ? (
                    <Col xs={12} md={12} className="d-flex flex-column">
                        <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                            <Container fluid className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                                <Navbar bg="white" expand="lg" className="mt-3 mb-0">
                                    <Navbar.Toggle aria-controls="basic-navbar-nav" />
                                    <Navbar.Collapse id="basic-navbar-nav">
                                        <Nav className="me-auto w-90" variant="tabs" activeKey={MENU_ITEMS.findIndex(item => item.section === selectedSection)}>
                                            {MENU_ITEMS.map((item, index) => (
                                                <Nav.Link
                                                    as={Link}
                                                    to={item.link}
                                                    key={index}
                                                    eventKey={index}
                                                    onClick={() => setSelectedSection(item.section)}
                                                    className={`position-relative survey-tab-link ${item.section === selectedSection ? "active-tab" : ""}`}
                                                >
                                                    {item.name}
                                                    {!!item.notifications &&
                                                        <Badge bg="success" className="position-absolute top-0 translate-middle badge rounded-pill">
                                                            {item.notifications < 10 ? item.notifications : "+9"}
                                                        </Badge>
                                                    }
                                                </Nav.Link>
                                            ))}
                                        </Nav>
                                    </Navbar.Collapse>
                                </Navbar>

                                <div className="d-flex flex-column flex-grow-1 align-items-stretch p-0 mt-0" style={{ minHeight: 0 }}>
                                    <Outlet context={{ claimUpdate, setClaimUpdate, setMessageError, sendEmail, setSendEmail, hideClaimActionButtons, setHideClaimActionButtons, errors, reloadClaim }} />
                                </div>
                            </Container>
                        </div>
                    </Col>
                ) : (
                    loading && (
                        <div className="overlay-spinner">
                            <Spinner animation="border" variant="light" />
                        </div>
                    )
                )}
            </Row>
        </div>
  );
}